import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";
import { defineMessages, FormattedMessage, injectIntl, intlShape } from "react-intl";
import { Alert } from "@yoast/ui-library";
import { speak } from "@wordpress/a11y";
import _debounce from "lodash/debounce";

import { SubscriptionProps } from "./SubscriptionsTable";
import SubscriptionsSection from "./SubscriptionsSection";
import { messages as tableMessages } from "./SubscriptionsTable/messages";
import AnimatedLoader from "../Loader";
import Search from "../Search/Search";
import SuggestedAction from "../SuggestedAction";
import { PageHeader } from "../PageHeader";
import SubscriptionDetailModal from "../modal/SubscriptionDetailModal";
import { ShopButton } from "../ShopButton";
import useSubscriptionSummary from "../../hooks/useSubscriptionSummary";

import * as styles from "./styles.module.scss";

const messages = defineMessages( {
	pageSubscriptionsLoaded: {
		id: "menu.account.subscriptions.loaded",
		defaultMessage: "Account subscriptions page loaded",
	},
	searchLabel: {
		id: "search.label.subscriptions",
		defaultMessage: "Search subscriptions",
	},
	searchResults: {
		id: "subscriptionsSearch.results",
		defaultMessage: "Number of subscriptions found: %d",
	},
	manageLicenseElsewhere: {
		id: "subscriptions.manageLicenceElsewhere",
		defaultMessage: "Subscriptions bought via a third party are managed by that provider.",
	},
	subscriptionsHeader: {
		id: "subscriptions.description",
		defaultMessage: "You can easily add and manage your subscriptions here. " +
				"Whether you're looking to renew your subscription(s) for another year, " +
				"add a site to your subscription(s), upgrade your subscription(s) to a bundle, " +
				"switch payment methods, or even bid farewell with a cancellation, " +
				"you're in the right place.",
	},
	subscriptionsHeaderTitle: {
		id: "subscriptions.title",
		defaultMessage: "Subscriptions",
	},
	loadFailed: {
		id: "subscriptions.loadFailed",
		defaultMessage: "We could not load your subscriptions. Please reload the page to try again.",
	},
} );

/**
 * The sections, in the order they stack down the page, with the headings they have always used.
 *
 * `needsAttention` leads because it is the only section that asks something of the customer, and
 * `inactive` is last because nobody has to act on it. All four render the same way: stacked and open, as
 * the page has always presented them.
 */
const SECTIONS = Object.freeze( [
	{ bucket: "needsAttention", title: tableMessages.needsAttentionTitle },
	{ bucket: "provisioned", title: tableMessages.partnerSubscriptionsTitle },
	{ bucket: "regular", title: tableMessages.subscriptionsTitle },
	{ bucket: "inactive", title: tableMessages.inactiveSubscriptionsTitle },
] );

const debouncedSpeak = _debounce( speak, 1000 );

/**
 * Returns the rendered SubscriptionsPage component.
 *
 * @param {Object} props The props to use.
 *
 * @returns {ReactElement} The rendered SubscriptionsPage component.
 */
const SubscriptionsPage = ( props ) => {
	const [ modalSubscription, setModalSubscription ] = useState( null );

	// The third-party-licence notice used to need its own request for the customer's licences. It is a flag
	// on the summary read model now, which every page in this app already shares one query for.
	const { summary } = useSubscriptionSummary();

	useEffect( () => {
		// Announce navigation to assistive technologies.
		speak( props.intl.formatMessage( messages.pageSubscriptionsLoaded ) );
	}, [] );

	// Every section matching the search, not just the one in front of the reader — the sections are stacked,
	// so all of them are on screen at once and a single section's total would understate the result.
	const matchCount = Object.values( props.bucketCounts ).reduce( ( sum, count ) => sum + count, 0 );

	// Whether any section is still settling. Announcing before they have is how a stale count gets read out.
	const isSettling = Object.values( props.sections )
		.some( section => section.isLoading || section.isPlaceholderData );

	// Keyed on the result arriving rather than on the keystroke, so the count spoken is the count for the
	// query that was actually run. Announcing on change would read out the previous result set's size.
	const spokenTotal = useRef( null );
	useEffect( () => {
		if ( props.query.length === 0 ) {
			// Forgotten on clear, or searching the same term again after clearing would announce nothing: the
			// count would match what was last spoken even though this is a new search.
			spokenTotal.current = null;
			return;
		}

		if ( isSettling || spokenTotal.current === matchCount ) {
			return;
		}

		spokenTotal.current = matchCount;
		debouncedSpeak(
			props.intl.formatMessage( messages.searchResults ).replace( /%d/, matchCount ),
			"assertive",
		);
	}, [ props.query, matchCount, isSettling ] );

	/**
	 * Return the search bar.
	 *
	 * @returns {ReactElement} The rendered Search component.
	 */
	const renderSearch = () => {
		return <div className={ styles.searchWrapper } key="subscriptionSearch">
			<Search
				id="search"
				searchLabel={ props.intl.formatMessage( messages.searchLabel ) }
				descriptionId="search-description"
				query={ props.query }
				onChange={ props.onSearchChange }
			/>
		</div>;
	};

	const noSubscriptionsParagraphs = [
		<FormattedMessage
			id="subscriptions.noSubscriptions.manage"
			key="subscriptions.noSubscriptions.manage"
			defaultMessage="When you buy one of our plugins or services, you start a new subscription which will be billed
			annually. At the end of each billing cycle you can choose to renew your subscription or let it expire."
		/>,
		<FormattedMessage
			id="subscriptions.noSubscriptions.pressButton"
			key="subscriptions.noSubscriptions.pressButton"
			defaultMessage="You don’t seem to have any subscriptions yet, so press the button below to visit our shop."
		/>,
	];

	const noSearchResultsParagraphs = [
		<FormattedMessage
			id="subscriptions.search.noResults"
			key="subscriptions.search.noResults"
			defaultMessage={ "We could not find any subscriptions matching { query }." }
			values={ { query: <strong>{ props.query }</strong> } }
		/>,
	];

	const notices = [];
	if ( summary.hasThirdPartyLicenses ) {
		notices.push( <Alert key="license-notice" variant="info">
			<FormattedMessage { ...messages.manageLicenseElsewhere } />
		</Alert> );
	}

	// Counts, not rows: each section shows one page, so an empty page while another section has entries would
	// otherwise read as an empty account. Showing "you have no subscriptions, go to the shop" to someone
	// whose search merely missed is the most damaging thing this page can do.
	const hasAnySubscriptions = Object.values( props.bucketCounts ).some( count => count > 0 );

	// Both of these have to be answered before the counts mean anything. Until a section resolves its counts
	// are the hook's zeroed placeholder, which is indistinguishable from an account that really holds
	// nothing — so without these branches a customer with 5000 subscriptions is told to visit the shop on
	// every cold load, and permanently whenever the request fails.
	const sectionStates = Object.values( props.sections );

	if ( ! hasAnySubscriptions && sectionStates.every( section => section.isError ) ) {
		return <>
			<PageHeader title={ messages.subscriptionsHeaderTitle } message={ messages.subscriptionsHeader } />
			<div className={ styles.subcriptionsPage }>
				<Alert variant="error">
					<FormattedMessage { ...messages.loadFailed } />
				</Alert>
			</div>
		</>;
	}

	// Every section, not any: the sections resolve independently, and one that has already answered with rows
	// is worth showing while the rest catch up.
	if ( ! hasAnySubscriptions && sectionStates.every( section => section.isLoading ) ) {
		return <>
			<PageHeader title={ messages.subscriptionsHeaderTitle } message={ messages.subscriptionsHeader } />
			<div className={ styles.subcriptionsPage }>
				<AnimatedLoader />
			</div>
		</>;
	}

	if ( hasAnySubscriptions ) {
		return <>
			<PageHeader title={ messages.subscriptionsHeaderTitle } message={ messages.subscriptionsHeader } />
			<div className={ styles.subcriptionsPage }>
				{ modalSubscription && <SubscriptionDetailModal
					onClose={ () => setModalSubscription( null ) }
					subscription={ modalSubscription }
					modalOpen={ true }
				/> }
				{ notices }
				{ renderSearch() }
				{ /* Stacked, in a fixed order, each with its own pagination. A section holding nothing renders
					nothing, so the page shows only the sections that apply — which is what it always did. */ }
				{ SECTIONS.map( ( { bucket, title } ) => (
					<SubscriptionsSection
						key={ bucket }
						bucket={ bucket }
						title={ title }
						count={ props.bucketCounts[ bucket ] }
						subscriptions={ props.sections[ bucket ].items }
						meta={ props.sections[ bucket ].meta }
						onPageChange={ page => props.onPageChange( bucket, page ) }
						onManage={ props.onManage }
						showDetailsModal={ setModalSubscription }
					/>
				) ) }
			</div>
		</>;
	}

	// A query that matched nothing. Distinguished from an empty account by the query being present, not by
	// the row count — both look identical from the rows alone.
	if ( props.query.length > 0 ) {
		return <>
			<PageHeader title={ messages.subscriptionsHeaderTitle } message={ messages.subscriptionsHeader } />
			<div className={ styles.subcriptionsPage }>
				{ notices }
				{ renderSearch() }
				<SuggestedAction paragraphs={ noSearchResultsParagraphs } />
			</div>
		</>;
	}

	return <>
		<PageHeader title={ messages.subscriptionsHeaderTitle } message={ messages.subscriptionsHeader } />
		<div className={ styles.subcriptionsPage }>
			{ notices }
			<SuggestedAction paragraphs={ noSubscriptionsParagraphs }>
				<div className={ styles.shopButton }>
					<ShopButton size="large" />
				</div>
			</SuggestedAction>
		</div>
	</>;
};

/** One section's slice of the response, as the hook returns it. */
const SectionProps = PropTypes.shape( {
	items: SubscriptionProps,
	meta: PropTypes.shape( {
		total: PropTypes.number.isRequired,
		page: PropTypes.number.isRequired,
		limit: PropTypes.number.isRequired,
		totalPages: PropTypes.number.isRequired,
	} ).isRequired,
	isLoading: PropTypes.bool,
	isError: PropTypes.bool,
	isPlaceholderData: PropTypes.bool,
} );

SubscriptionsPage.propTypes = {
	onSearchChange: PropTypes.func.isRequired,
	onPageChange: PropTypes.func.isRequired,
	onManage: PropTypes.func.isRequired,
	intl: intlShape.isRequired,
	query: PropTypes.string,
	sections: PropTypes.shape( {
		needsAttention: SectionProps.isRequired,
		provisioned: SectionProps.isRequired,
		regular: SectionProps.isRequired,
		inactive: SectionProps.isRequired,
	} ).isRequired,
	bucketCounts: PropTypes.shape( {
		needsAttention: PropTypes.number.isRequired,
		regular: PropTypes.number.isRequired,
		provisioned: PropTypes.number.isRequired,
		inactive: PropTypes.number.isRequired,
	} ).isRequired,
};

SubscriptionsPage.defaultProps = {
	query: "",
};

export default injectIntl( SubscriptionsPage );
